import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchHubChanges } from '../api/api';
import { HubChangesResponse } from '../types/hubChanges';
import { useCardPage } from '../contexts/CardPageContext';

/**
 * "PriceHub 변경" 목록 조회(2026-10-21) — 가격 조회 화면 진입 때, 그리고 ① 수정 가격 조회 · 시세 대조 ·
 * ③ 스토어 반영 잡이 끝날 때마다 다시 받는다(목록의 근거가 로컬 수정가 vs 기준가라 잡 뒤에 바뀐다).
 * 마지막에 시작한 요청의 응답만 반영한다(NaverSyncLogPage 와 같은 reqSeq 패턴).
 */
export function useHubChanges(enabled = true) {
  const { jobs, cards } = useCardPage();
  const [data, setData]       = useState<HubChangesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<Error | null>(null);
  // 지금 data 를 낸 요청이 시작된 시각 — 요청을 보낸 뒤 늦게 도착한 '그 전에 시작한' 조회가 응답 불명 표시를 지우지 않게
  // 표가 비교한다(코덱 리뷰 #209-1, 감사 card-controltower#281).
  const [dataStartedAt, setDataStartedAt] = useState<number | null>(null);

  const reqSeq = useRef(0);
  const reload = useCallback(() => {
    const seq = ++reqSeq.current;
    const startedAt = Date.now();
    setLoading(true);
    setError(null);
    return fetchHubChanges()
      .then((res) => {
        if (seq === reqSeq.current) { setData(res); setDataStartedAt(startedAt); }
      })
      .catch((e: unknown) => {
        if (seq === reqSeq.current) setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => { if (seq === reqSeq.current) setLoading(false); });
  }, []);

  // enabled=false(가격 조회 세 단계 화면)면 저절로 받지 않는다 — reload() 를 직접 부를 때만.
  useEffect(() => { if (enabled) reload(); }, [reload, enabled]);

  // 잡이 끝나는 순간(true → false)에만 다시 받는다 — 진행 중 여러 번 렌더돼도 한 번.
  const prevRef = useRef({ p: jobs.priceUpdating, c: jobs.classifying, n: jobs.naverSyncing });
  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = { p: jobs.priceUpdating, c: jobs.classifying, n: jobs.naverSyncing };
    if (!enabled) return;
    if ((prev.p && !jobs.priceUpdating) || (prev.c && !jobs.classifying) || (prev.n && !jobs.naverSyncing)) {
      reload();
    }
  }, [jobs.priceUpdating, jobs.classifying, jobs.naverSyncing, reload, enabled]);

  // 카드 목록을 서버에서 다시 받을 때마다(다른 창의 ① · 롤백 · 가져오기 · 새로고침 등 어느 경로든) PriceHub 변경 목록도
  // 다시 받는다 — 화면 진입 · 잡 종료 때만 받으면 '가격 상승' 탭(카드 목록 기준)과 건수가 어긋났다(감사 card-controltower#121).
  // 마운트 때 값은 위의 진입 조회가 맡으므로 건너뛴다. 요청 차례 번호는 그대로라 겹쳐도 마지막 응답만 남는다.
  const cardsVersion = cards?.loadVersion;
  const seenVersionRef = useRef(cardsVersion);
  // reloadWithCards 로 이미 직접 받았으면 바로 뒤따르는 카드 재조회 한 번은 건너뛴다(같은 목록 두 번 받지 않게). 짝지은 카드
  // 조회가 실패하면 바로 내린다 — 시간으로만 두면 그 사이 다른 경로의 카드 재조회를 삼켰다(코덱 셀프 리뷰 PR #57 중간).
  const skipNextVersionRef = useRef(false);
  useEffect(() => {
    if (seenVersionRef.current === cardsVersion) return;
    seenVersionRef.current = cardsVersion;
    if (skipNextVersionRef.current) { skipNextVersionRef.current = false; return; }
    // 첫 조회(0 → 1)도 따라 받는다 — 카드 첫 조회가 실패했다가 다시 시도로 처음 성공한 경우를 놓치지 않게(코덱 셀프 리뷰).
    // 앱 시작과 동시에 이 화면을 열 때만 진입 조회와 겹쳐 한 번 더 받는다(차례 번호로 마지막 응답만 남는다).
    if (enabled) reload();
  }, [cardsVersion, enabled, reload]);

  /**
   * 카드 목록과 함께 다시 받기(새로고침 · 다시 시도) — 카드 조회 성공에만 묶지 않고 지금 바로 받는다. 카드 API 가 계속 실패해도
   * PriceHub 변경 목록은 다시 받는다(코덱 리뷰 #209-5, 감사 #285). 카드 재조회도 여기서 부르고, 그 성공으로 생기는 재조회 한 번은
   * 건너뛴다(실패하면 건너뛰기를 바로 내린다).
   */
  const reloadWithCards = useCallback((reloadCards: () => Promise<boolean | void>) => {
    skipNextVersionRef.current = true;
    void reloadCards().then((ok) => { if (ok === false) skipNextVersionRef.current = false; });
    return reload();
  }, [reload]);

  return { data, loading, error, reload, reloadWithCards, dataStartedAt };
}
